import { useEffect } from "react";
import TextField from "./CustomTextField";
import { Box, FormControlLabel, Radio, RadioGroup, MenuItem, Typography } from "@mui/material";
import { CostRow, Custom, measurementUnit } from "./calculator";
import { customFieldSx, customNumberProps as numberProps } from "./customStyles";
type Props = {
  row: CostRow;
  item: Custom;
  onChange: (patch: Partial<CostRow>) => void;
  readOnly: boolean;
  busy: boolean;
  settings: any;
};
export default function CustomMaterialCalculation({
  row,
  item,
  onChange,
  readOnly,
  busy,
  settings,
}: Props) {
  const linkedUnit = measurementUnit(item.unit);
  const sizeUnit =
    row.jobSizeUnit ??
    (linkedUnit === "lin_ft" && row.calculation !== "coverage" ? "sq_ft" : linkedUnit) ??
    item.areaUnit ??
    "sq_ft";
  const legacySeparate =
    !row.jobSizeUnit &&
    (row.calculation === "coverage" ? !linkedUnit : item.areaSource === "manual");
  const size = row.jobSize ?? (legacySeparate ? item.area : item.quantity) ?? "0";
  const compatible = !!linkedUnit && (row.calculation === "coverage" || linkedUnit !== "lin_ft");
  const separate = !compatible || row.jobSize != null || legacySeparate;
  // Persist forced separate measurements so later header edits cannot change them.
  useEffect(() => {
    if (!readOnly && !busy && row.calculation !== "manual" && !compatible && row.jobSize == null) {
      onChange({ jobSize: size, jobSizeUnit: sizeUnit });
    }
  }, [compatible, row.calculation, row.jobSize, readOnly, busy, size, sizeUnit, onChange]);
  return (
    <Box sx={{ p: 1, bgcolor: "#F9FAFC", ...customFieldSx }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
          py: 0.5,
          "& .MuiTextField-root": { width: "100%", minWidth: 0 },
        }}
      >
        <TextField
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          select
          size="small"
          sx={{ gridColumn: "1 / -1" }}
          label="Quantity calculation"
          value={row.calculation}
          disabled={readOnly || busy}
          onChange={(e) => {
            const calculation = e.target.value as CostRow["calculation"];
            const weight = calculation === "density" || calculation === "thickness_factor";
            const unit =
              weight &&
              !["ton", "tons", "us_ton", "lb", "lbs", "pound", "pounds"].includes(
                row.unit.toLowerCase()
              )
                ? "Ton"
                : row.unit;
            const measurement = weight && linkedUnit === "lin_ft" ? "sq_ft" : linkedUnit ?? "sq_ft";
            onChange({
              calculation,
              unit,
              jobSize: linkedUnit && (!weight || linkedUnit !== "lin_ft") ? null : size,
              jobSizeUnit: measurement,
              factorAreaUnit: calculation === "coverage" ? measurement : row.factorAreaUnit,
              quantityOverride: null,
            });
          }}
        >
          <MenuItem value="manual">Manual quantity</MenuItem>
          <MenuItem value="coverage">Coverage Rate</MenuItem>
          <MenuItem value="density">Weight — density</MenuItem>
          <MenuItem value="thickness_factor">Weight — thickness-specific factor</MenuItem>
        </TextField>
        {row.calculation !== "manual" && (
          <>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <RadioGroup
                row
                aria-label="Measurement source"
                sx={{
                  gap: { xs: 0.5, sm: 1.5 },
                  "& .MuiFormControlLabel-root": { m: 0, minHeight: { xs: 40, sm: 28 } },
                  "& .MuiFormControlLabel-label": {
                    fontSize: 12,
                    fontWeight: 400,
                    color: "text.secondary",
                  },
                  "& .MuiRadio-root": { p: 0.5 },
                  "& .MuiSvgIcon-root": { fontSize: 16 },
                }}
                value={separate ? "different" : "product"}
                onChange={(event) => {
                  if (event.target.value === "product")
                    onChange({ jobSize: null, jobSizeUnit: linkedUnit });
                  else onChange({ jobSize: size, jobSizeUnit: sizeUnit });
                }}
              >
                <FormControlLabel
                  value="product"
                  control={<Radio size="small" />}
                  label="Use Job Size"
                  disabled={!compatible || readOnly || busy}
                />
                <FormControlLabel
                  value="different"
                  control={<Radio size="small" />}
                  label="Use different measurement"
                  disabled={readOnly || busy}
                />
              </RadioGroup>
              <Typography variant="caption">
                {compatible
                  ? !separate
                    ? `${item.quantity} ${item.unit} — linked to job size`
                    : "This measurement stays independent of the job size."
                  : "The product unit is incompatible. Enter a separate measurement."}
              </Typography>
            </Box>
            {separate && (
              <>
                <TextField
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  type="text"
                  label="Quantity / Job Size"
                  inputProps={{ ...numberProps, inputMode: "decimal" }}
                  value={size}
                  disabled={readOnly || busy}
                  onChange={(e) => onChange({ jobSize: e.target.value, jobSizeUnit: sizeUnit })}
                />
                <TextField
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  select
                  size="small"
                  label="Job Size Unit"
                  value={sizeUnit}
                  disabled={readOnly || busy}
                  onChange={(e) =>
                    onChange({
                      jobSizeUnit: e.target.value,
                      jobSize: size,
                    })
                  }
                >
                  <MenuItem value="sq_ft">Sq Ft</MenuItem>
                  <MenuItem value="sq_yd">Sq Yd</MenuItem>
                  {row.calculation === "coverage" && <MenuItem value="lin_ft">Lin Ft</MenuItem>}
                </TextField>
              </>
            )}
            <Box
              sx={
                row.calculation === "coverage"
                  ? {
                      gridColumn: "1 / -1",
                      display: "grid",
                      gridTemplateColumns: "minmax(0, .9fr) minmax(0, 1fr) auto minmax(0, 1.15fr)",
                      alignItems: "center",
                      gap: 1,
                      "& .MuiInputLabel-root": { fontSize: 12 },
                    }
                  : { display: "contents" }
              }
            >
              <TextField
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                size="small"
                type="number"
                inputProps={numberProps}
                label={
                  row.calculation === "coverage"
                    ? "Coverage Rate"
                    : row.calculation === "density"
                    ? "Density (lb/cu ft)"
                    : "US tons/sq ft (thickness included)"
                }
                value={row.factor ?? ""}
                disabled={readOnly || busy}
                onChange={(e) =>
                  onChange({
                    factor: e.target.value,
                    factorLabel: null,
                  })
                }
              />
              {row.calculation === "coverage" && (
                <TextField
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  select
                  size="small"
                  label="Coverage Unit"
                  value={row.factorAreaUnit ?? "sq_ft"}
                  disabled={readOnly || busy}
                  onChange={(e) => onChange({ factorAreaUnit: e.target.value })}
                >
                  <MenuItem value="sq_ft">Sq Ft</MenuItem>
                  <MenuItem value="sq_yd">Sq Yd</MenuItem>
                  <MenuItem value="lin_ft">Lin Ft</MenuItem>
                </TextField>
              )}
              {row.calculation === "coverage" && (
                <>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    Per
                  </Typography>
                  <Typography
                    aria-label="Material unit"
                    sx={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}
                  >
                    {row.unit === "Cubic Yd" ? "Cubic Yard" : row.unit}
                  </Typography>
                </>
              )}
            </Box>
            {row.calculation === "density" && (
              <TextField
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                size="small"
                type="number"
                inputProps={numberProps}
                label="Thickness (inches)"
                value={row.thickness ?? ""}
                disabled={readOnly || busy}
                onChange={(e) => onChange({ thickness: e.target.value })}
              />
            )}
            {row.calculation === "thickness_factor" && (
              <TextField
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                select
                size="small"
                label="Use organization factor"
                value=""
                disabled={readOnly || busy}
                sx={{ minWidth: 200 }}
                onChange={(e) => {
                  const factor = settings?.customSettings?.factors?.find(
                    (f: any) => f.label === e.target.value
                  );
                  if (factor)
                    onChange({
                      factor: factor.value,
                      factorLabel: factor.label,
                    });
                }}
              >
                <MenuItem value="">Select a starting value</MenuItem>
                {(settings?.customSettings?.factors ?? []).map((f: any) => (
                  <MenuItem key={f.label} value={f.label}>
                    {f.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </>
        )}
      </Box>
      {row.factorLabel && (
        <Typography variant="caption">
          Starting factor: {row.factorLabel} (saved with this item)
        </Typography>
      )}
    </Box>
  );
}
